import { useCallback, useEffect, useRef, useState } from 'react'
import { API_URL } from './config'
import { getAccessToken } from './session'

/* ==========================================================================
 * Server-sent events
 * ======================================================================== */

export interface UseSSEOptions {
  /** Named events to listen for. Each is delivered to `onEvent`. */
  events?: string[]
  onEvent?: (event: string, data: unknown) => void
  enabled?: boolean
}

export interface UseSSEResult {
  connected: boolean
  error: Error | null
}

/**
 * Subscribe to an SSE stream.
 *
 * EventSource cannot set headers, so the platform accepts the token as a query
 * parameter for these routes. Reconnects are left to EventSource itself, which
 * already implements backoff.
 */
export function useSSE(
  url: string | null,
  { events = [], onEvent, enabled = true }: UseSSEOptions = {}
): UseSSEResult {
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Keep the latest handler without re-opening the stream on every render.
  const handlerRef = useRef(onEvent)
  handlerRef.current = onEvent

  useEffect(() => {
    if (!enabled || !url) return

    const source = new EventSource(url)

    source.onopen = () => {
      setConnected(true)
      setError(null)
    }

    source.onerror = () => {
      setConnected(false)
      // EventSource retries on its own; surface it but do not tear down.
      setError(new Error('Live connection interrupted. Reconnecting…'))
    }

    const listeners = events.map((name) => {
      const listener = (event: MessageEvent) => {
        try {
          handlerRef.current?.(name, JSON.parse(event.data))
        } catch {
          handlerRef.current?.(name, event.data)
        }
      }
      source.addEventListener(name, listener as EventListener)
      return { name, listener }
    })

    return () => {
      for (const { name, listener } of listeners) {
        source.removeEventListener(name, listener as EventListener)
      }
      source.close()
      setConnected(false)
    }
  }, [url, enabled, events.join(',')])

  return { connected, error }
}

/** Absolute URL for a public SSE route, e.g. /status/events. */
export function sseUrl(path: string): string {
  return `${API_URL}${path}`
}

/** Absolute URL for an authenticated SSE route, with the bearer as a param. */
export function authenticatedSseUrl(path: string): string | null {
  const token = getAccessToken()
  if (!token) return null
  return `${API_URL}${path}?token=${encodeURIComponent(token)}`
}

/* ==========================================================================
 * WebSocket push, with a polling fallback
 * ======================================================================== */

/** The frame the platform's push channel sends: `{ event, data }`. */
export interface PushFrame {
  event: string
  data: unknown
}

export interface UseWebSocketOptions {
  /** Receives every frame. Check `frame.event` to filter. */
  onMessage?: (frame: PushFrame) => void
  enabled?: boolean
}

export interface UseWebSocketResult {
  /** 'live' once the socket is open, 'degraded' when polling instead. */
  mode: 'connecting' | 'live' | 'degraded'
  lastMessage: PushFrame | null
}

const MAX_BACKOFF_MS = 30_000

/**
 * Subscribe to the platform's browser push channel (`GET /ws`).
 *
 * The wire format is `{ event, data }` per platform/ws/hub.go. The push channel
 * is newer than the rest of the API, so failure is expected rather than
 * exceptional: after the first failed connection the hook reports `degraded`
 * and the caller is expected to poll instead. Retries continue with
 * exponential backoff, so a recovered server restores live updates on its own.
 */
export function useWebSocket(
  path = '/ws',
  { onMessage, enabled = true }: UseWebSocketOptions = {}
): UseWebSocketResult {
  const [mode, setMode] = useState<UseWebSocketResult['mode']>('connecting')
  const [lastMessage, setLastMessage] = useState<PushFrame | null>(null)

  const handlerRef = useRef(onMessage)
  handlerRef.current = onMessage

  const attemptRef = useRef(0)
  const closedRef = useRef(false)

  const connect = useCallback(() => {
    const token = getAccessToken()
    if (!token) return null

    const scheme = API_URL.startsWith('https') ? 'wss' : 'ws'
    const base = API_URL.replace(/^https?/, scheme)

    try {
      return new WebSocket(`${base}${path}?token=${encodeURIComponent(token)}`)
    } catch {
      return null
    }
  }, [path])

  useEffect(() => {
    if (!enabled) return
    closedRef.current = false

    let socket: WebSocket | null = null
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const open = () => {
      if (closedRef.current) return
      socket = connect()

      if (!socket) {
        setMode('degraded')
        return
      }

      socket.onopen = () => {
        attemptRef.current = 0
        setMode('live')
      }

      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data) as PushFrame
          setLastMessage(parsed)
          handlerRef.current?.(parsed)
        } catch {
          // Ignore frames that are not JSON — keepalives and the like.
        }
      }


      socket.onclose = () => {
        if (closedRef.current) return
        // Cannot reconnect with a dead session; the app will redirect to sign in.
        if (!getAccessToken()) {
          setMode('degraded')
          return
        }

        attemptRef.current += 1
        // Degrade immediately on the first failure so the caller starts
        // polling rather than waiting on a socket that may never open.
        setMode('degraded')

        const delay = Math.min(1000 * 2 ** (attemptRef.current - 1), MAX_BACKOFF_MS)
        retryTimer = setTimeout(open, delay)
      }

      socket.onerror = () => {
        // onclose always follows; backoff is handled there.
        setMode('degraded')
      }
    }

    open()

    return () => {
      closedRef.current = true
      if (retryTimer) clearTimeout(retryTimer)
      socket?.close()
    }
  }, [connect, enabled])

  return { mode, lastMessage }
}
